import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ShoppingCart, DollarSign, Users, Clock, ChefHat, TrendingUp, TrendingDown, Minus, BarChart3, PieChart as PieIcon } from 'lucide-react';
import SuperAdminDashboard from './SuperAdminDashboard';
import useDashboard from '@/hooks/useDashboard';

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

const KPI_SHELL = [
  { key: 'k0' },
  { key: 'k1' },
  { key: 'k2' },
  { key: 'k3' },
  { key: 'k4' },
];

const TrendPill = ({ trend }) => {
  if (!trend || !trend.comparable || trend.delta_percentage === null) {
    return (
      <div className="mt-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>
        No previous data
      </div>
    );
  }

  const delta = Number(trend.delta_percentage || 0);
  const positive = delta > 0;
  const negative = delta < 0;
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const color = positive ? '#10B981' : negative ? '#EF4444' : '#64748B';

  return (
    <div className="mt-2 inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs" style={{ fontFamily: 'Figtree, sans-serif', color, backgroundColor: positive ? '#ECFDF3' : negative ? '#FEF2F2' : '#F1F5F9' }}>
      <Icon className="h-3.5 w-3.5" />
      <span>{positive ? '+' : ''}{delta.toFixed(1)}%</span>
    </div>
  );
};

const EmptyChartState = ({ icon: Icon, message }) => {
  return (
    <div className="flex h-[300px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border/60 bg-muted/20 text-center">
      <Icon className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
        {message}
      </p>
    </div>
  );
};

const getOrdersTitle = (period, t) => {
  if (period === 'today') return t('ordersToday');
  if (period === 'yesterday') return t('ordersYesterday');
  if (period === 'last_7_days') return t('ordersLast7Days');
  if (period === 'last_30_days') return t('ordersLast30Days');
  if (period === 'this_month') return t('ordersThisMonth');
  return t('orders');
};

const getSalesTitle = (period, t) => {
  if (period === 'today') return t('salesToday');
  if (period === 'yesterday') return t('salesYesterday');
  if (period === 'last_7_days') return t('salesLast7Days');
  if (period === 'last_30_days') return t('salesLast30Days');
  if (period === 'this_month') return t('salesThisMonth');
  return t('sales');
};

const RestaurantDashboard = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('last_7_days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [appliedCustomStartDate, setAppliedCustomStartDate] = useState('');
  const [appliedCustomEndDate, setAppliedCustomEndDate] = useState('');

  const startDate = period === 'custom' ? appliedCustomStartDate : undefined;
  const endDate = period === 'custom' ? appliedCustomEndDate : undefined;
  const customRangeReady = period !== 'custom' || Boolean(appliedCustomStartDate && appliedCustomEndDate);

  const { data, loading, error } = useDashboard({
    autoFetch: customRangeReady,
    period,
    startDate,
    endDate,
  });
  const [lastUpdated, setLastUpdated] = useState(null);

  const stats = data?.restaurant_dashboard?.stats || null;
  const trends = data?.restaurant_dashboard?.stats_trends || {};
  const salesData = data?.restaurant_dashboard?.sales_data || [];
  const statusData = data?.restaurant_dashboard?.status_data || [];
  const dateRange = data?.date_range || null;

  const selectedPeriodLabel = useMemo(() => {
    return PERIOD_OPTIONS.find((item) => item.value === period)?.label || 'Last 7 Days';
  }, [period]);

  const customRangeLabel = useMemo(() => {
    if (period !== 'custom') {
      return '';
    }
    if (dateRange?.current_start && dateRange?.current_end) {
      const start = new Date(dateRange.current_start);
      const end = new Date(dateRange.current_end);
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    }
    if (appliedCustomStartDate && appliedCustomEndDate) {
      return `${appliedCustomStartDate} - ${appliedCustomEndDate}`;
    }
    return '';
  }, [period, dateRange, appliedCustomStartDate, appliedCustomEndDate]);

  useEffect(() => {
    if (data) {
      setLastUpdated(new Date());
    }
  }, [data]);

  const applyCustomRange = () => {
    setAppliedCustomStartDate(customStartDate);
    setAppliedCustomEndDate(customEndDate);
  };

  if (error) return <div className="text-center py-8">Failed to load dashboard data.</div>;

  const statCards = [
    {
      key: 'orders_today',
      title: getOrdersTitle(period, t),
      value: stats?.orders_today || 0,
      icon: ShoppingCart,
      color: '#232D4B',
      subtitle: period === 'custom' ? customRangeLabel : '',
    },
    {
      key: 'sales_today',
      title: getSalesTitle(period, t),
      value: `RM${Number(stats?.sales_today || 0).toFixed(2)}`,
      icon: DollarSign,
      color: '#E57200',
      subtitle: period === 'custom' ? customRangeLabel : '',
    },
    {
      key: 'customers',
      title: t('customers'),
      value: stats?.customers || 0,
      icon: Users,
      color: '#10B981',
    },
    {
      key: 'pending_orders',
      title: 'Pending Orders',
      value: stats?.pending_orders || 0,
      icon: Clock,
      color: '#F59E0B',
    },
    {
      key: 'kitchen_queue',
      title: 'Kitchen Queue',
      value: stats?.kitchen_queue || 0,
      icon: ChefHat,
      color: '#EF4444',
    },
  ];

  const COLORS = ['#232D4B', '#E57200', '#10B981', '#F59E0B'];
  const hasSalesData = salesData.length > 0;
  const hasStatusData = statusData.some((entry) => Number(entry.count || 0) > 0);

  return (
    <div className="space-y-6" data-testid="restaurant-dashboard">
      <div>
        <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#232D4B' }} data-testid="dashboard-title">
          {t('dashboard')}
        </h1>
        <p className="text-muted-foreground mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
          {t('welcomeBack')}
        </p>
        <p className="text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
          {t('restaurantPerformanceLine')}
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-end gap-3">
        <div className="w-full md:w-64">
          <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Date Range
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger>
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((item) => (
                <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {period === 'custom' && (
          <>
            <div>
              <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
                Start
              </div>
              <Input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
                End
              </div>
              <Input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
            </div>
            <Button
              className="md:self-end"
              style={{ backgroundColor: '#E57200' }}
              onClick={applyCustomRange}
              disabled={!customStartDate || !customEndDate}
            >
              Apply
            </Button>
          </>
        )}
      </div>

      <div className="text-sm text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
        {`Showing data for ${dateRange?.label || selectedPeriodLabel}. Comparing with previous equivalent period.`}
      </div>

      <div className="text-xs text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
        {`Last updated: ${lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '--:--'}`}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {loading ? KPI_SHELL.map((item, i) => (
          <Card key={item.key} className="border-border/40" data-testid={`stat-card-skeleton-${i}`}>
            <CardHeader className="pb-2">
              <Skeleton className="h-3 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-7 w-20" />
              <Skeleton className="mt-3 h-5 w-24 rounded-full" />
            </CardContent>
          </Card>
        )) : statCards.map((stat, i) => {
          const Icon = stat.icon;
          const trend = trends?.[stat.key];
          return (
            <Card key={i} className="border-border/40 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`stat-card-${i}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  {stat.title}
                </CardTitle>
                <Icon className="h-4 w-4" style={{ color: stat.color }} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: stat.color }}>
                  {stat.value}
                </div>
                {stat.subtitle ? (
                  <div className="mt-1 text-[11px] text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    {stat.subtitle}
                  </div>
                ) : null}
                <TrendPill trend={trend} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border/40">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{t('salesAnalytics')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : hasSalesData ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={salesData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="_id" stroke="#64748B" />
                  <YAxis stroke="#64748B" />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total_sales" stroke="#E57200" strokeWidth={2.5} name="Sales" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState icon={BarChart3} message="No orders during this period." />
            )}
          </CardContent>
        </Card>

        <Card className="border-border/40">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{t('orderStatusAnalytics')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : hasStatusData ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={statusData} dataKey="count" nameKey="_id" cx="50%" cy="50%" outerRadius={100} label>
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState icon={PieIcon} message="No order status data for this period." />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  
  if (user?.role === 'super_admin') {
    return <SuperAdminDashboard />;
  }
  
  return <RestaurantDashboard />;
};

export default Dashboard;
